import { useId, useRef, useState } from 'react';
import { useT } from '../i18n';
import { ACCEPTED_FILE_ACCEPT_ATTR, type SelectedFile } from '../domain/types';
import './FileInput.css';

interface FileInputProps {
  label: string;
  help: string;
  cta: string;
  name: string;
  error?: string | undefined;
  multiple?: boolean;
  optional?: boolean;
  files: SelectedFile[];
  onAdd: (files: File[]) => void;
  onRemove: (id: string) => void;
}

/** Romanian file sizes: comma decimal, and a non-breaking space before the unit. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) {
    return `${new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 0 }).format(bytes / 1024)} kB`;
  }
  return `${new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 1 }).format(bytes / (1024 * 1024))} MB`;
}

export function FileInput({
  label,
  help,
  cta,
  name,
  error,
  multiple = false,
  optional = false,
  files,
  onAdd,
  onRemove,
}: FileInputProps) {
  const t = useT();
  const id = useId();
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);

  const handleFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    onAdd(Array.from(list));
    // Reset the native input, or picking the same file twice in a row is a no-op.
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="file-input">
      <div className="field__label" id={`${id}-label`}>
        {label}
        {optional && <span className="field__optional">{t.step2.additionalOptional}</span>}
      </div>

      <div
        className={dragging ? 'file-input__drop file-input__drop--active' : 'file-input__drop'}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
      >
        {/*
          A real <input type="file">, visually hidden but focusable, with the
          button as its label. Keyboard and screen-reader users get the native
          picker; everyone else gets a control that matches the rest of the form.
        */}
        <input
          ref={inputRef}
          id={id}
          className="visually-hidden file-input__native"
          type="file"
          accept={ACCEPTED_FILE_ACCEPT_ATTR}
          multiple={multiple}
          data-field={name}
          aria-labelledby={`${id}-label`}
          aria-describedby={[error ? errorId : null, helpId].filter(Boolean).join(' ')}
          aria-invalid={Boolean(error)}
          onChange={(e) => handleFiles(e.target.files)}
        />
        <label className="btn file-input__cta" htmlFor={id}>
          {files.length > 0 && !multiple ? t.step2.certificateCtaReplace : cta}
        </label>
        <span className="file-input__drop-hint">{t.step2.certificateDrop}</span>
      </div>

      <p className="field__help" id={helpId}>
        {help}
      </p>

      {error && (
        <p className="field__error" id={errorId}>
          <span className="visually-hidden">{t.validation.fieldError}</span>
          <span aria-hidden="true">↳</span>
          {error}
        </p>
      )}

      {files.length > 0 && (
        <ul className="file-input__list">
          {files.map((entry) => (
            <li key={entry.id} className="file-input__item">
              <span className="file-input__name" title={entry.file.name}>
                {entry.file.name}
              </span>
              <span className="file-input__size">{formatBytes(entry.file.size)}</span>
              <button
                type="button"
                className="btn-link file-input__remove"
                onClick={() => onRemove(entry.id)}
                aria-label={t.step2.fileRemoveNamed(entry.file.name)}
              >
                {t.step2.fileRemove}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
